import { Link } from "react-router-dom";
import { ArrowRight, Building2, Zap, Shield, Wind, Camera, Activity } from "lucide-react";
import HexagonField from "@/components/HexagonField";
import { usePageTitle } from "@/hooks/usePageTitle";

const IMG = "https://images.pexels.com/photos/36564992/pexels-photo-36564992.jpeg";

const Tile = ({ icon: Icon, title, copy }) => (
  <div className="glass rounded-2xl p-6 hover:border-white/25 transition-colors">
    <Icon className="w-5 h-5 text-accent" />
    <div className="mt-4 font-display text-lg">{title}</div>
    <p className="text-sm text-white/60 mt-2 leading-relaxed">{copy}</p>
  </div>
);

export default function IndustrialAutomation() {
  usePageTitle(
    "Industrial Automation",
    "Custom automation for factories, workshops, and industrial spaces. RF, sensors, edge intelligence, and integration built to the shape of your operation."
  );
  return (
    <div>
      <section className="relative min-h-[78vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 opacity-35"
             style={{ backgroundImage: `linear-gradient(0deg, #0A0B10 25%, rgba(10,11,16,0.4) 100%), url(${IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <HexagonField />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-28">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-mono">Prime · Industrial + Commercial Automation</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold mt-5 leading-[1.02] tracking-tighter max-w-4xl">
            Plants and buildings that <span className="gradient-text">quietly compound margin</span>.
          </h1>
          <p className="mt-7 text-lg text-white/70 max-w-2xl leading-relaxed">
            Automation that earns its keep. Building management, energy, access control, surveillance, and HVAC —
            integrated into systems that cut cost, tighten security, and keep running on their own.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full gradient-bg font-medium hover:shadow-[0_0_30px_rgba(157,76,221,0.5)]">
              Book a site walkthrough <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/marketplace" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 hover:bg-white/5">
              Sourced from WeAutomate catalog
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Service catalog</div>
          <h2 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter max-w-2xl">
            What lives <span className="text-accent">under the hood</span>.
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Tile icon={Building2} title="Building Management (BMS)" copy="Single pane of glass for lighting, HVAC, access, alarms, occupancy and energy." />
            <Tile icon={Zap} title="Energy management" copy="Sub-metering, anomaly detection, peak-shaving, demand-response readiness." />
            <Tile icon={Shield} title="Access control" copy="Multi-factor access, visitor management, occupancy-aware safety routines." />
            <Tile icon={Wind} title="HVAC orchestration" copy="VRV/VRF, AHU, fan-coil — interlocked with occupancy and weather." />
            <Tile icon={Camera} title="Surveillance" copy="IP cameras, ANPR, AI analytics, NVR — sized to your scale." />
            <Tile icon={Activity} title="Conference + office automation" copy="Boardrooms that just work — A/V, scheduling, lighting, blinds, room sensors." />
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="glass rounded-3xl p-10 lg:p-14 grid lg:grid-cols-2 gap-10 items-center noise relative overflow-hidden">
            <div>
              <h3 className="font-display text-3xl lg:text-4xl tracking-tighter">
                Built on the <span className="text-accent">same engineering bench</span> that builds your phones and locks.
              </h3>
              <p className="mt-5 text-white/70 leading-relaxed">
                Our industrial work isn't a separate division. The team that ships your COB lights and your BMS
                comes from the same OEM/ODM bench — which is why retrofitting, integration and post-handover
                support don't feel like a different company.
              </p>
            </div>
            <div className="font-mono text-xs space-y-3 text-white/60">
              <div>// site recipe</div>
              <div>1 · audit existing infra + protocols</div>
              <div>2 · pick gateways + sensors from WeAutomate stack</div>
              <div>3 · commission BMS layer + dashboards</div>
              <div>4 · train your facilities team</div>
              <div>5 · 6 / 12 / 24 month performance reviews</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
